import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { featureFlagService } from '../services/featureFlagService';
import { useEffect } from 'react';

/**
 * React hook for checking feature flags
 *
 * Feature flags are loaded from the API once per page load and cached in global state.
 *
 * **Caveat**: while that one-time load is still in flight, this returns `false` for every
 * flag (the loading default), not the flag's real value. That's fine for values read during
 * render (the component re-renders once the real value arrives). It is NOT fine for a
 * one-time mount effect (`useEffect(..., [])`) that reads the flag and never re-runs — if
 * flags haven't loaded yet at that instant, the effect permanently acts on `false` and never
 * self-corrects. For that shape, use `useFeatureFlagWithLoading` or gate on
 * `useFeatureFlagsReady()` instead. See docs/FEATURE_FLAGS_GUIDE.md "Avoiding the mount-effect
 * loading race" for a real example of this bug.
 *
 * @param flagName - Name of the feature flag to check
 * @returns True if the feature flag is enabled, false otherwise (including while loading)
 *
 * @example
 * ```tsx
 * const sendCaseEnabled = useFeatureFlag('SendCaseEnabled');
 *
 * if (sendCaseEnabled) {
 *   return <SendCaseButton />;
 * }
 * return null;
 * ```
 */
export function useFeatureFlag(flagName: string): boolean {
    const { flags } = useFeatureFlagContext();
    
    // Update the service's flags getter when flags change
    useEffect(() => {
        featureFlagService.setFlagsGetter(() => flags);
    }, [flags]);
    
    return flags[flagName] ?? false;
}

/**
 * React hook for getting all feature flags
 * 
 * Feature flags are loaded from the API at startup and cached in global state.
 * 
 * @returns Dictionary of all feature flags with their enabled status
 * 
 * @example
 * ```tsx
 * const flags = useAllFeatureFlags();
 * 
 * if (flags.SendCaseEnabled) {
 *   return <SendCaseButton />;
 * }
 * ```
 */
export function useAllFeatureFlags(): Record<string, boolean> {
    const { flags } = useFeatureFlagContext();

    // Update the service's flags getter when flags change
    useEffect(() => {
        featureFlagService.setFlagsGetter(() => flags);
    }, [flags]);

    return flags;
}

/**
 * True once the one-time feature-flag fetch has resolved (success or failure). False only
 * during the brief window right after the app mounts.
 *
 * Use this to gate a `useEffect` that makes a one-time decision (redirect, arm a timer, etc.)
 * based on a flag, alongside your other dependencies — e.g.
 * `if (!flagsReady) return;` plus `flagsReady` in the dependency array — so the effect
 * re-evaluates once the real flag value arrives instead of permanently acting on the `false`
 * loading-default. See docs/FEATURE_FLAGS_GUIDE.md "Avoiding the mount-effect loading race".
 *
 * If a single flag drives the whole decision, prefer `useFeatureFlagWithLoading` instead —
 * it bundles the flag's value with this same readiness check in one call.
 *
 * @example
 * ```tsx
 * const dashboardEnabled = useFeatureFlag('AdminDashboard');
 * const flagsReady = useFeatureFlagsReady();
 *
 * useEffect(() => {
 *   if (!flagsReady) return;
 *   if (path === '/admin') navigate(dashboardEnabled ? '/admin/dashboard' : '/admin/teams');
 * }, [path, dashboardEnabled, flagsReady]);
 * ```
 */
export function useFeatureFlagsReady(): boolean {
    const { isLoading } = useFeatureFlagContext();
    return !isLoading;
}

/**
 * Like `useFeatureFlag`, but bundles the flag's value with whether it's trustworthy yet.
 * Prefer this over `useFeatureFlag` for a one-time mount effect that reads a single flag —
 * the returned `ready` flag can't be silently overlooked the way a bare `isLoading` check
 * living in a different hook call can. See docs/FEATURE_FLAGS_GUIDE.md "Avoiding the
 * mount-effect loading race".
 *
 * @param flagName - Name of the feature flag to check
 * @returns `{ enabled, ready }` — `enabled` is the flag's value (false while loading, same as
 *   `useFeatureFlag`); `ready` is true once the one-time fetch has resolved.
 *
 * @example
 * ```tsx
 * const { enabled: notificationsEnabled, ready } = useFeatureFlagWithLoading('Notifications');
 *
 * useEffect(() => {
 *   if (!ready || !notificationsEnabled) return;
 *   // arm the one-time tutorial — fires once flags are actually loaded, not just at mount
 * }, [ready, notificationsEnabled]);
 * ```
 */
export function useFeatureFlagWithLoading(flagName: string): { enabled: boolean; ready: boolean } {
    const enabled = useFeatureFlag(flagName);
    const ready = useFeatureFlagsReady();
    return { enabled, ready };
}
